import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import toast from 'react-hot-toast';

const NAVIONICS_CSS = 'https://webapiv2.navionics.com/dist/webapi/webapi.min.css';
const NAVIONICS_SCRIPT = 'https://webapiv2.navionics.com/dist/webapi/webapi.min.no-dep.js';

let navionicsLoadPromise = null;

function loadNavionicsWebApi() {
  if (window.JNC?.Leaflet?.NavionicsOverlay) {
    return Promise.resolve(window.JNC);
  }
  if (navionicsLoadPromise) return navionicsLoadPromise;

  navionicsLoadPromise = new Promise((resolve, reject) => {
    if (!document.querySelector(`link[href="${NAVIONICS_CSS}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = NAVIONICS_CSS;
      document.head.appendChild(link);
    }

    const existing = document.querySelector(`script[src="${NAVIONICS_SCRIPT}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.JNC));
      existing.addEventListener('error', reject);
      return;
    }

    const script = document.createElement('script');
    script.src = NAVIONICS_SCRIPT;
    script.async = true;
    script.onload = () => resolve(window.JNC);
    script.onerror = () => reject(new Error('Failed to load Navionics Web API'));
    document.head.appendChild(script);
  });

  return navionicsLoadPromise;
}

/**
 * Embedded Navionics nautical charts via Garmin Web API v2 + Leaflet overlay.
 * Requires NAVIONICS_WEB_API_KEY (Enhanced tier for vessel overlays on BoatWise).
 */
export default function NavionicsChartLayer({ navKey, chartType = 'nautical' }) {
  const map = useMap();
  const overlayRef = useRef(null);

  useEffect(() => {
    if (!map || !navKey) return;

    let cancelled = false;

    loadNavionicsWebApi()
      .then((JNC) => {
        if (cancelled || !JNC?.Leaflet?.NavionicsOverlay) return;

        if (overlayRef.current) {
          map.removeLayer(overlayRef.current);
          overlayRef.current = null;
        }

        const type =
          chartType === 'sonar'
            ? JNC.NAVIONICS_CHARTS.SONARCHART
            : JNC.NAVIONICS_CHARTS.NAUTICAL;

        const overlay = new JNC.Leaflet.NavionicsOverlay({
          navKey,
          chartType: type,
          isTransparent: false,
          logoPayoff: true,
          zIndex: 1,
        });

        overlay.addTo(map);
        overlayRef.current = overlay;
      })
      .catch((err) => {
        console.error('[Navionics] Chart layer failed:', err);
        toast.error('Navionics charts failed to load — check API key');
      });

    return () => {
      cancelled = true;
      if (overlayRef.current) {
        map.removeLayer(overlayRef.current);
        overlayRef.current = null;
      }
    };
  }, [map, navKey, chartType]);

  return null;
}
